import React from 'react';

// Only this font family is used throughout the app.
const fontFamily = "'Outfit', sans-serif";

const fontFamilyMono = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

// Raw size scale (px).
const fontSize = {
    xxs: 9,
    xs: 10,
    sm: 11,
    base: 12,
    md: 13,
    lg: 14,
    xl: 16,
    '2xl': 18,
    '3xl': 20,
    '4xl': 22,
    '5xl': 24,
    '6xl': 26,
    '7xl': 30,
};

const fontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};

// Spacing scale (px) — for padding / margin / gap / radius, so those
// values are tokenised too instead of hardcoded.
const spacing = {
    xs: 2,
    sm: 4,
    md: 6,
    lg: 8,
    xl: 10,
    '2xl': 14,
    '3xl': 16,
    '4xl': 32,
};

// Each preset is a FUNCTION returning a style object ready to spread into
// an inline `style={...}`. Pass overrides to tweak size/weight/colour:
//   <h2 style={heading2()}>…</h2>
//   <p style={body({ color: '#fff' })}>…</p>
const preset = (size, weight) => (overrides = {}) => ({
    fontFamily,
    fontSize: size,
    fontWeight: weight,
    ...overrides,
});

// Page / section headings.
// heading1 / heading2 are NOT exported — nothing imports them directly. They
// exist only to back the <Heading> component's variants 1 and 2 (see
// HEADING_VARIANTS below). heading3 is exported (used across the app).
const heading1 = preset(fontSize['7xl'], fontWeight.bold);     // 30 / 700
const heading2 = preset(fontSize['5xl'], fontWeight.bold);     // 24 / 700
const heading3 = preset(fontSize['3xl'], fontWeight.semibold); // 20 / 600

// Sub-headings
const subHeading1 = preset(fontSize['2xl'], fontWeight.semibold); // 18 / 600
const subHeading2 = preset(fontSize.xl, fontWeight.semibold);     // 16 / 600
const subHeading3 = preset(fontSize.lg, fontWeight.medium);       // 14 / 500

// Body copy
const body = preset(fontSize.md, fontWeight.regular);      // 13 / 400
const bodySmall = preset(fontSize.base, fontWeight.regular); // 12 / 400

// Supporting text
const label = preset(fontSize.sm, fontWeight.medium);   // 11 / 500
const caption = preset(fontSize.xs, fontWeight.regular); // 10 / 400

// ---------------------------------------------------------------------------
// Reusable text COMPONENTS — the single source of truth for on-screen type.
//
// These are ADDITIVE: every preset above (heading1, body, …) stays exactly as
// it is and keeps working for the files that already import it. The components
// render THROUGH those presets for size/weight, then apply their role's font
// family (see HEADING_FAMILY / BODY_FAMILY below) — headings use Outfit, body
// uses Mulish.
//
// Prefer these over a raw <h*>/<p>/<span> with an inline preset style. Because
// every heading / sub-heading / body line routes through the same three
// components, changing a size, weight or family globally is a one-line edit
// HERE — nothing in the feature code needs touching.
//
//   <Heading>Page title</Heading>                    // h2, 24/700
//   <Heading variant={3} as="h1">Smaller</Heading>   // heading3 preset on <h1>
//   <SubHeading>Section</SubHeading>                  // h3, 18/600
//   <SubHeading variant={2}>Card title</SubHeading>  // h3, 16/600
//   <Body>Paragraph copy</Body>                       // p, 13/400
//   <Body variant={2} as="span">Small</Body>          // span, 12/400
//
// Props: `variant` (which size in the family), `as` (element to render),
// `color` (shortcut for style.color), `weight` (a fontWeight token key or a
// raw number), `style` (any extra overrides), plus className / onClick / etc.
const HEADING_VARIANTS = { 1: heading1, 2: heading2, 3: heading3 };
const SUB_HEADING_VARIANTS = { 1: subHeading1, 2: subHeading2, 3: subHeading3 };
const BODY_VARIANTS = { 1: body, 2: bodySmall };

// Font family + size per text role — the ONE place to change the app's type.
// The presets above give each variant its size/weight; these role styles then
// override the family (and, for headings, pin a fixed size) so a global swap is
// a one-line edit. A caller can still override per-instance via `style`.
// Headings / sub-headings use Outfit; body uses Mulish (loaded via the Google
// Fonts <link> in public/index.html).
// Default text colour: headings / sub-headings are black; body is #000000ba.
// A caller can override per-instance via the `color` prop or, when a
// `className` is supplied, via that class's CSS.
const HEADING_FAMILY = { fontFamily: 'Outfit, sans-serif', fontSize: fontSize['4xl'], color: '#000000' };    // Outfit 22
const SUB_HEADING_FAMILY = { fontFamily: 'Outfit, sans-serif', fontSize: fontSize['3xl'], color: '#000000' }; // Outfit 20
// Body default colour: a neutral "mid black" (#3a3a3a) — the single source for
// plain body text, so callers should NOT pass a per-instance `color` just to get
// the standard text tone. Override via `color` only for genuinely different tones.
const BODY_FAMILY = { fontFamily: "'Mulish'", fontStyle: 'normal', fontSize: fontSize['md'], color: '#3a3a3a', fontWeight: fontWeight.medium }; // Mulish normal

const createTextComponent = (variants, defaultVariant, defaultTag, familyStyle) => {
    const Text = ({ as: Tag = defaultTag, variant = defaultVariant, color, weight, style, className, children, ...rest }) => {
        const presetFn = variants[variant] || variants[defaultVariant];
        // Default colour is baked into familyStyle. If the caller passes a
        // `className` but no explicit `color`, drop the inline colour so the
        // class's CSS can win (inline styles otherwise beat class rules).
        const { color: familyColor, ...familyRest } = familyStyle;
        const baseColor = color ? { color } : (className ? {} : { color: familyColor });
        const overrides = {
            ...familyRest,
            ...baseColor,
            ...(weight ? { fontWeight: fontWeight[weight] || weight } : {}),
            ...style,
        };
        return (
            <Tag className={className} style={presetFn(overrides)} {...rest}>
                {children}
            </Tag>
        );
    };
    return Text;
};

// Page / section headings (h2 by default) — Outfit 22.
const Heading = createTextComponent(HEADING_VARIANTS, 2, 'h2', HEADING_FAMILY);
// Sub-headings — card / block titles (h3 by default) — Outfit 20.
const SubHeading = createTextComponent(SUB_HEADING_VARIANTS, 1, 'h3', SUB_HEADING_FAMILY);
// Body copy (p by default; use `as="span"` inline) — Mulish, normal style.
const Body = createTextComponent(BODY_VARIANTS, 1, 'p', BODY_FAMILY);

const fonts = {
    fontFamily,
    fontFamilyMono,
    fontSize,
    fontWeight,
    spacing,
    heading3,
    subHeading1,
    subHeading2,
    subHeading3,
    body,
    bodySmall,
    label,
    caption,
    Heading,
    SubHeading,
    Body,
};

export {
    fontFamily,
    fontFamilyMono,
    fontSize,
    fontWeight,
    spacing,
    heading3,
    subHeading1,
    subHeading2,
    subHeading3,
    body,
    bodySmall,
    label,
    caption,
    Heading,
    SubHeading,
    Body,
};
export default fonts;
